import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, Bot, Send, Settings, CheckCircle,
  Brain, FileText, Users, Package, TrendingUp,
  Bell, Zap, Shield, BarChart, Clock,
  RefreshCw, HelpCircle, Activity, Wrench, Sparkles,
  AlertTriangle, Database, Key
} from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';

interface Command {
  command: string;
  description: string;
  category: string;
  examples?: string[];
  requiresAuth?: boolean;
  icon: React.ReactNode;
}

export const TelegramAIAssistant: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'commands' | 'ai' | 'workflows' | 'analytics'>('ai');
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<string>('');
  const [botToken, setBotToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('https://api.plexisystem.pl/webhook/telegram');
  
  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 500,
    contextMemory: true,
    autoSuggestions: true,
    voiceEnabled: false,
    language: 'pl',
    responseFormat: 'detailed',
    intelligentRouting: true,
    learningMode: false
  });

  const [workflows, setWorkflows] = useState([
    {
      id: 1,
      name: 'Zatwierdzanie ofert',
      description: 'Szybkie zatwierdzanie ofert przez Telegram',
      enabled: true,
      triggers: ['oferta_nowa', 'oferta_zmiana'],
      actions: ['powiadomienie', 'przycisk_zatwierdz', 'eskalacja']
    },
    {
      id: 2,
      name: 'Raport dzienny',
      description: 'Automatyczny raport o 9:00 każdego dnia',
      enabled: true,
      triggers: ['cron_9am'],
      actions: ['generuj_raport', 'wyslij_telegram', 'zapisz_pdf']
    },
    {
      id: 3,
      name: 'Alert niski stan magazynowy',
      description: 'Powiadomienie gdy stan < minimum',
      enabled: true,
      triggers: ['magazyn_niski_stan'],
      actions: ['powiadomienie', 'sugestia_zamowienia', 'utworz_zadanie']
    }
  ]);

  const [analytics] = useState({
    totalCommands: 1247,
    aiQueries: 892,
    avgResponseTime: '1.2s',
    satisfaction: 94,
    topCommands: [
      { command: '/raport', count: 234 },
      { command: '/klient', count: 189 },
      { command: '/oferta', count: 156 },
      { command: '/produkcja', count: 134 },
      { command: '/ai', count: 112 }
    ],
    recentActivity: [
      { time: '10:23', user: 'Jan Kowalski', command: '/raport sprzedaz', status: 'success' },
      { time: '10:15', user: 'Anna Nowak', command: '/ai analiza klienta XYZ', status: 'success' },
      { time: '09:45', user: 'System', command: 'Automatyczny raport dzienny', status: 'success' },
      { time: '09:30', user: 'Piotr Wiśniewski', command: '/oferta 2024/123', status: 'success' }
    ]
  });

  // Pobierz ustawienia z Supabase przy załadowaniu
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { supabase } = await import('../../config/supabase');
      
      // Użyj maybeSingle() zamiast single() - obsługuje 0 lub 1 wiersz
      const { data, error } = await supabase
        .from('telegram_ai_settings')
        .select('*')
        .maybeSingle();
      
      if (data && !error) {
        setAiSettings({
          enabled: data.enabled,
          model: data.model,
          temperature: data.temperature,
          maxTokens: data.max_tokens,
          contextMemory: data.context_memory,
          autoSuggestions: data.auto_suggestions,
          voiceEnabled: data.voice_enabled,
          language: data.language,
          responseFormat: data.response_format,
          intelligentRouting: data.intelligent_routing,
          learningMode: data.learning_mode
        });
        
        if (data.telegram_bot_token) {
          setBotToken(data.telegram_bot_token);
        }
        if (data.webhook_url) {
          setWebhookUrl(data.webhook_url);
        }
      }
    } catch (error) {
      console.error('Błąd ładowania ustawień:', error);
    }
  };

  const commands: Command[] = [
    // AI Commands
    {
      command: '/ai [pytanie]',
      description: 'Zadaj pytanie asystentowi AI',
      category: 'AI',
      examples: [
        '/ai jaka jest marża na produkcie X',
        '/ai kto jest najlepszym klientem',
        '/ai prognoza sprzedaży na Q2'
      ],
      icon: <Brain className="w-4 h-4" />
    },
    {
      command: '/analiza [temat]',
      description: 'Szczegółowa analiza AI',
      category: 'AI',
      examples: [
        '/analiza rentowność klienta ABC',
        '/analiza trendy sprzedażowe',
        '/analiza optymalizacja produkcji'
      ],
      icon: <TrendingUp className="w-4 h-4" />
    },
    {
      command: '/sugestia [obszar]',
      description: 'Rekomendacje AI',
      category: 'AI',
      examples: [
        '/sugestia cennik',
        '/sugestia cross-sell dla klienta XYZ',
        '/sugestia optymalizacja kosztów'
      ],
      icon: <Sparkles className="w-4 h-4" />
    },
    
    // Raporty
    {
      command: '/raport [typ] [okres]',
      description: 'Generuj raport',
      category: 'Raporty',
      examples: [
        '/raport sprzedaz dzis',
        '/raport produkcja tydzien',
        '/raport finanse miesiac'
      ],
      icon: <FileText className="w-4 h-4" />
    },
    {
      command: '/dashboard',
      description: 'Pokaż główne KPI',
      category: 'Raporty',
      icon: <BarChart className="w-4 h-4" />
    },
    
    // CRM
    {
      command: '/klient [nazwa]',
      description: 'Informacje o kliencie',
      category: 'CRM',
      examples: ['/klient ABC Company'],
      icon: <Users className="w-4 h-4" />
    },
    {
      command: '/oferta [numer]',
      description: 'Status oferty',
      category: 'CRM',
      examples: ['/oferta 2024/123'],
      icon: <FileText className="w-4 h-4" />
    },
    
    // Produkcja
    {
      command: '/produkcja [status]',
      description: 'Status produkcji',
      category: 'Produkcja',
      examples: ['/produkcja', '/produkcja opoznienia'],
      icon: <Wrench className="w-4 h-4" />
    },
    {
      command: '/magazyn [produkt]',
      description: 'Stan magazynowy',
      category: 'Produkcja',
      examples: ['/magazyn plexi-3mm'],
      icon: <Package className="w-4 h-4" />
    },
    
    // Workflow
    {
      command: '/zatwierdz [dokument]',
      description: 'Zatwierdź dokument',
      category: 'Workflow',
      examples: ['/zatwierdz oferta-123'],
      requiresAuth: true,
      icon: <Shield className="w-4 h-4" />
    },
    
    // System
    {
      command: '/pomoc [komenda]',
      description: 'Pomoc dotycząca komendy',
      category: 'System',
      icon: <HelpCircle className="w-4 h-4" />
    },
    {
      command: '/status',
      description: 'Status systemu',
      category: 'System',
      icon: <Activity className="w-4 h-4" />
    }
  ];

  const handleSaveAISettings = async () => {
    try {
      setIsLoading(true);
      const { supabase } = await import('../../config/supabase');
      
      // Użyj stałego ID dla pojedynczego rekordu
      const SETTINGS_ID = '65cd161a-c11f-44e7-bbcf-0712c687e8f9';
      
      // Upsert z konkretnym ID
      const { data, error } = await supabase
        .from('telegram_ai_settings')
        .upsert({
          id: SETTINGS_ID,
          enabled: aiSettings.enabled,
          model: aiSettings.model,
          temperature: aiSettings.temperature,
          max_tokens: aiSettings.maxTokens,
          context_memory: aiSettings.contextMemory,
          auto_suggestions: aiSettings.autoSuggestions,
          voice_enabled: aiSettings.voiceEnabled,
          language: aiSettings.language,
          response_format: aiSettings.responseFormat,
          intelligent_routing: aiSettings.intelligentRouting,
          learning_mode: aiSettings.learningMode,
          telegram_bot_token: botToken,
          webhook_url: webhookUrl,
          settings: JSON.stringify(aiSettings),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        })
        .select()
        .maybeSingle();

      if (error) {
        console.error('Błąd Supabase:', error);
        if (error.code === '42P01') {
          alert('⚠️ Tabele nie istnieją w bazie danych.\n\nUruchom skrypt SQL:\nfix-telegram-tables.sql\n\nw Supabase Dashboard → SQL Editor');
        } else {
          alert(`Błąd zapisywania: ${error.message}`);
        }
      } else {
        alert('✅ Ustawienia AI zapisane pomyślnie!');
        
        // Jeśli jest token, skonfiguruj webhook
        if (botToken) {
          await configureTelegramWebhook();
        }
      }
    } catch (error) {
      console.error('Błąd zapisywania ustawień AI:', error);
      alert('Błąd zapisywania ustawień. Sprawdź konsolę.');
    } finally {
      setIsLoading(false);
    }
  };

  const configureTelegramWebhook = async () => {
    if (!botToken) {
      alert('❌ Wprowadź token bota Telegram');
      return;
    }
    
    try {
      // Ustaw webhook w Telegram API
      const response = await fetch(`https://api.telegram.org/bot${botToken}/setWebhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: webhookUrl,
          allowed_updates: ['message', 'callback_query']
        })
      });
      
      const result = await response.json();
      if (result.ok) {
        alert('✅ Webhook Telegram skonfigurowany pomyślnie!');
      } else {
        alert(`❌ Błąd konfiguracji webhook: ${result.description}`);
      }
    } catch (error) {
      console.error('Błąd konfiguracji webhook:', error);
      alert('❌ Błąd konfiguracji webhook');
    }
  };

  const handleToggleWorkflow = async (workflowId: number) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (workflow) {
      workflow.enabled = !workflow.enabled;
      setWorkflows([...workflows]);
      
      try {
        const { supabase } = await import('../../config/supabase');
        
        const { error } = await supabase
          .from('telegram_workflows')
          .update({ 
            enabled: workflow.enabled,
            updated_at: new Date().toISOString()
          })
          .eq('id', workflowId);

        if (error) {
          console.error('Błąd Supabase:', error);
          workflow.enabled = !workflow.enabled;
          setWorkflows([...workflows]);
        }
      } catch (error) {
        console.error('Błąd przełączania workflow:', error);
        workflow.enabled = !workflow.enabled;
        setWorkflows([...workflows]);
      }
    }
  };

  const handleTestCommand = async (command: string) => {
    try {
      setIsLoading(true);
      setTestResult('');
      
      const { supabase } = await import('../../config/supabase');
      
      // Wywołaj funkcję produkcyjną w Supabase
      const { data, error } = await supabase.rpc('process_telegram_command', {
        p_command: command,
        p_user_id: 'test_user',
        p_user_name: 'Test User'
      });

      if (error) {
        console.error('Błąd wykonania komendy:', error);
        
        // Jeśli funkcja nie istnieje
        if (error.code === '42883') {
          alert('⚠️ Funkcja process_telegram_command nie istnieje.\n\nUruchom skrypt SQL: fix-telegram-tables.sql');
        } else {
          setTestResult(`❌ Błąd: ${error.message}`);
        }
        return;
      }

      // Formatuj odpowiedź
      let formattedResponse = '';
      if (data) {
        switch (data.type) {
          case 'report':
            formattedResponse = `📊 ${data.title}\n`;
            if (data.data) {
              Object.entries(data.data).forEach(([key, value]) => {
                formattedResponse += `• ${key}: ${value}\n`;
              });
            }
            break;
            
          case 'client':
            if (data.data) {
              formattedResponse = `👤 Klient: ${data.data.name}\n`;
              formattedResponse += `• Email: ${data.data.email}\n`;
              formattedResponse += `• Telefon: ${data.data.phone}\n`;
            } else {
              formattedResponse = 'Nie znaleziono klienta';
            }
            break;
            
          case 'offer':
            if (data.data) {
              formattedResponse = `📄 Oferta: ${data.data.number}\n`;
              formattedResponse += `• Status: ${data.data.status}\n`;
              formattedResponse += `• Wartość: ${data.data.total_net} PLN\n`;
            } else {
              formattedResponse = 'Nie znaleziono oferty';
            }
            break;
            
          case 'inventory':
            formattedResponse = '📦 Stan magazynowy:\n';
            if (data.data && Array.isArray(data.data)) {
              data.data.forEach(item => {
                formattedResponse += `• ${item.material_name}: ${item.quantity} ${item.unit}\n`;
              });
            } else {
              formattedResponse += 'Brak danych magazynowych';
            }
            break;
            
          case 'ai_query':
            formattedResponse = `🤖 ${data.message}\n`;
            formattedResponse += `Zapytanie: "${data.query}"`;
            break;
            
          case 'error':
            formattedResponse = `❌ ${data.message}`;
            break;
            
          default:
            formattedResponse = JSON.stringify(data, null, 2);
        }
      }
      
      setTestResult(formattedResponse || '✅ Komenda wykonana pomyślnie');
      
    } catch (error) {
      console.error('Błąd testowania komendy:', error);
      setTestResult('❌ Błąd wykonania komendy. Sprawdź konsolę.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderAITab = () => (
    <div className="space-y-6">
      {/* WYŁĄCZONE - Bot używa konfiguracji z API Settings */}
      <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-6">
        <h3 className="text-yellow-400 font-semibold mb-2">⚠️ Konfiguracja przeniesiona</h3>
        <p className="text-gray-300">Konfiguracja AI została przeniesiona do modułu API Settings.</p>
        <p className="text-gray-400 text-sm mt-2">Bot automatycznie używa klucza API z:</p>
        <a href="/api-settings" className="text-blue-400 hover:text-blue-300 underline">Ustawienia → API Settings → telegram_bot</a>
      </div>
      
      {/* Ukryj resztę panelu */}
      <div style={{display: 'none'}}>
      {/* Token bota i webhook */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-yellow-500" />
          Konfiguracja bota Telegram
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Token bota (z @BotFather)
            </label>
            <input
              type="password"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
            />
            <p className="text-xs text-gray-400 mt-1">
              Uzyskaj token od @BotFather na Telegram
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              URL Webhook
            </label>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
            />
            <p className="text-xs text-gray-400 mt-1">
              URL gdzie Telegram będzie wysyłał wiadomości
            </p>
          </div>
          
          <button
            onClick={configureTelegramWebhook}
            disabled={!botToken || isLoading}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            Skonfiguruj webhook
          </button>
        </div>
      </div>
      
      {/* Ustawienia modelu AI */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-500" />
          Konfiguracja asystenta AI
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Model AI
            </label>
            <select
              value={aiSettings.model}
              onChange={(e) => setAiSettings({...aiSettings, model: e.target.value})}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
            >
              <option value="gpt-4">GPT-4 (Najlepszy)</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Szybki)</option>
              <option value="claude-3">Claude 3 (Zaawansowany)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Język odpowiedzi
            </label>
            <select
              value={aiSettings.language}
              onChange={(e) => setAiSettings({...aiSettings, language: e.target.value})}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
            >
              <option value="pl">Polski</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Temperatura (kreatywność)
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={aiSettings.temperature}
              onChange={(e) => setAiSettings({...aiSettings, temperature: parseFloat(e.target.value)})}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Precyzyjny</span>
              <span>{aiSettings.temperature}</span>
              <span>Kreatywny</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Max tokenów
            </label>
            <input
              type="number"
              value={aiSettings.maxTokens}
              onChange={(e) => setAiSettings({...aiSettings, maxTokens: parseInt(e.target.value)})}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
            />
          </div>
        </div>
        
        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={aiSettings.contextMemory}
              onChange={(e) => setAiSettings({...aiSettings, contextMemory: e.target.checked})}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Pamięć kontekstu</p>
              <p className="text-xs text-gray-400">AI pamięta poprzednie pytania w sesji</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={aiSettings.autoSuggestions}
              onChange={(e) => setAiSettings({...aiSettings, autoSuggestions: e.target.checked})}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Auto-sugestie</p>
              <p className="text-xs text-gray-400">AI proponuje następne kroki</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={aiSettings.intelligentRouting}
              onChange={(e) => setAiSettings({...aiSettings, intelligentRouting: e.target.checked})}
              className="w-4 h-4 text-orange-500"
            />
            <div>
              <p className="text-white">Inteligentny routing</p>
              <p className="text-xs text-gray-400">AI automatycznie kieruje zapytania do właściwych osób</p>
            </div>
          </label>
        </div>
        
        <button
          onClick={handleSaveAISettings}
          disabled={isLoading}
          className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
        >
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Settings className="w-4 h-4" />
          )}
          Zapisz ustawienia AI
        </button>
      </div>
      
      {/* Test komendy */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-green-500" />
          Test komendy (produkcyjna baza danych)
        </h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['/raport', '/klient ABC', '/oferta 2024/123', '/magazyn', '/produkcja', '/ai test'].map(cmd => (
              <button
                key={cmd}
                onClick={() => handleTestCommand(cmd)}
                disabled={isLoading}
                className="px-3 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 disabled:opacity-50 text-sm"
              >
                {cmd}
              </button>
            ))}
          </div>
          
          {testResult && (
            <div className="p-4 bg-zinc-900 rounded-lg">
              <pre className="text-sm text-gray-300 whitespace-pre-wrap">{testResult}</pre>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );

  const renderCommandsTab = () => (
    <div className="space-y-6">
      {Object.entries(
        commands.reduce((acc, cmd) => {
          if (!acc[cmd.category]) acc[cmd.category] = [];
          acc[cmd.category].push(cmd);
          return acc;
        }, {} as Record<string, Command[]>)
      ).map(([category, categoryCommands]) => (
        <div key={category} className="bg-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{category}</h3>
          
          <div className="space-y-3">
            {categoryCommands.map((cmd, index) => (
              <div key={index} className="bg-zinc-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {cmd.icon}
                      <code className="text-orange-400 font-mono">{cmd.command}</code>
                      {cmd.requiresAuth && (
                        <span className="px-2 py-0.5 bg-red-600 text-xs rounded-full">
                          Wymaga autoryzacji
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-300">{cmd.description}</p>
                    
                    {cmd.examples && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-400 mb-1">Przykłady:</p>
                        <div className="space-y-1">
                          {cmd.examples.map((example, i) => (
                            <code key={i} className="block text-xs text-blue-400 bg-zinc-800 px-2 py-1 rounded">
                              {example}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleTestCommand(cmd.examples ? cmd.examples[0] : cmd.command)}
                    disabled={isLoading}
                    className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Test
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderWorkflowsTab = () => (
    <div className="space-y-6">
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Automatyczne workflow
        </h3>
        
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="bg-zinc-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">{workflow.name}</h4>
                  <p className="text-sm text-gray-300 mb-3">{workflow.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-2">
                    <div className="text-xs text-gray-400">Wyzwalacze:</div>
                    {workflow.triggers.map((trigger, i) => (
                      <span key={i} className="px-2 py-1 bg-zinc-600 text-xs rounded">
                        {trigger}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <div className="text-xs text-gray-400">Akcje:</div>
                    {workflow.actions.map((action, i) => (
                      <span key={i} className="px-2 py-1 bg-zinc-600 text-xs rounded">
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={() => handleToggleWorkflow(workflow.id)}
                  className={`ml-4 px-4 py-2 rounded-lg transition-all ${
                    workflow.enabled 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-zinc-600 text-gray-300 hover:bg-zinc-500'
                  }`}
                >
                  {workflow.enabled ? 'Aktywny' : 'Wyłączony'}
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <button className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
          <Zap className="w-4 h-4" />
          Dodaj nowy workflow
        </button>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Statystyki ogólne */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <MessageCircle className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold text-white">{analytics.totalCommands}</span>
          </div>
          <p className="text-sm text-gray-400">Wszystkie komendy</p>
          <p className="text-xs text-green-400 mt-1">+12% vs ostatni miesiąc</p>
        </div>
        
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Brain className="w-8 h-8 text-purple-500" />
            <span className="text-2xl font-bold text-white">{analytics.aiQueries}</span>
          </div>
          <p className="text-sm text-gray-400">Zapytania AI</p>
          <p className="text-xs text-green-400 mt-1">+28% vs ostatni miesiąc</p>
        </div>
        
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-orange-500" />
            <span className="text-2xl font-bold text-white">{analytics.avgResponseTime}</span>
          </div>
          <p className="text-sm text-gray-400">Średni czas odpowiedzi</p>
          <p className="text-xs text-green-400 mt-1">-0.3s vs ostatni miesiąc</p>
        </div>
        
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 text-green-500" />
            <span className="text-2xl font-bold text-white">{analytics.satisfaction}%</span>
          </div>
          <p className="text-sm text-gray-400">Satysfakcja</p>
          <p className="text-xs text-green-400 mt-1">+2% vs ostatni miesiąc</p>
        </div>
      </div>
      
      {/* Najpopularniejsze komendy */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Najpopularniejsze komendy</h3>
        
        <div className="space-y-3">
          {analytics.topCommands.map((cmd, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-gray-400">#{index + 1}</span>
                <code className="text-orange-400 font-mono">{cmd.command}</code>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white font-semibold">{cmd.count}</span>
                <div className="w-32 bg-zinc-700 rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full"
                    style={{ width: `${(cmd.count / analytics.topCommands[0].count) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Ostatnia aktywność */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Ostatnia aktywność</h3>
          <button className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
            <RefreshCw className="w-4 h-4" />
            Odśwież
          </button>
        </div>
        
        <div className="space-y-3">
          {analytics.recentActivity.map((activity, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-zinc-700 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{activity.time}</span>
                <span className="text-white">{activity.user}</span>
                <code className="text-sm text-blue-400">{activity.command}</code>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                activity.status === 'success' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-red-600 text-white'
              }`}>
                {activity.status === 'success' ? 'Sukces' : 'Błąd'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Nagłówek z zakładkami */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bot className="w-8 h-8 text-purple-500" />
            Asystent AI Telegram (Produkcyjny)
          </h2>
          
          <div className="flex items-center gap-2">
            {botToken ? (
              <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Bot skonfigurowany
              </span>
            ) : (
              <span className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-full flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Wymagana konfiguracja
              </span>
            )}
            {botToken && (
              <a
                href="https://t.me/plexisystem_ai_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Otwórz czat
              </a>
            )}
          </div>
        </div>
        
        {/* Zakładki */}
        <div className="flex gap-2 border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-4 py-2 font-medium transition-all ${
              activeTab === 'ai'
                ? 'text-purple-500 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Brain className="w-4 h-4 inline mr-2" />
            Asystent AI
          </button>
          <button
            onClick={() => setActiveTab('commands')}
            className={`px-4 py-2 font-medium transition-all ${
              activeTab === 'commands'
                ? 'text-purple-500 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Komendy
          </button>
          <button
            onClick={() => setActiveTab('workflows')}
            className={`px-4 py-2 font-medium transition-all ${
              activeTab === 'workflows'
                ? 'text-purple-500 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Workflow
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-all ${
              activeTab === 'analytics'
                ? 'text-purple-500 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <BarChart className="w-4 h-4 inline mr-2" />
            Analityka
          </button>
        </div>
      </div>
      
      {/* Zawartość zakładki */}
      {activeTab === 'ai' && renderAITab()}
      {activeTab === 'commands' && renderCommandsTab()}
      {activeTab === 'workflows' && renderWorkflowsTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}
    </div>
  );
};